import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../config/api';
import { GOOGLE_MAPS_API_KEY, loadGoogleMaps } from '../lib/googleMaps';
import {
  STATUS_LABELS,
  buildTimeline,
  deriveAppStates,
  extendRangeWithTracks,
  deriveStudentStates,
  describeEvent,
  ALL_EVENT_GROUPS,
  GROUP_LABELS,
  countEventsByGroup,
  eventGroup,
  filterEventsForActor,
  formatClock,
  formatDuration,
  formatOffset,
  interpolatePosition,
  lastEventIndexAt,
  lastPointIndexAt,
  summarizeStatuses,
  toMs,
} from '../lib/replayPlayback';

const SPEEDS = [1, 2, 5, 10, 30, 60];
const EVENT_PAGE_SIZE = 2000;
const FEED_LIMIT = 250;

const GROUP_COLORS = {
  dismissal: '#2a78d6',
  geofence: '#1baf7a',
  interaction: '#7c3aed',
  lifecycle: '#b45309',
  permission: '#e34948',
  other: '#52514e',
};

const STATUS_COLORS = {
  waiting: '#9a9895',
  called: '#2a78d6',
  sent: '#1baf7a',
  picked_up: '#0f766e',
};

// Pickup-zone geofences, kept distinct from every track colour.
const ZONE_COLOR = '#0f766e';

// Distinct hues for parent tracks on the map.
const TRACK_COLORS = ['#2a78d6', '#1baf7a', '#e34948', '#7c3aed', '#b45309', '#0891b2', '#c2410c', '#4d7c0f'];

function trackColor(index) {
  return TRACK_COLORS[index % TRACK_COLORS.length];
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(days) {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

/**
 * Project lat/lng onto an SVG viewport.
 *
 * The replay draws its own map rather than pulling in a tile provider: the
 * useful frame of reference here is the pickup zones and the tracks relative to
 * each other, and this works with no API key and no external requests.
 */
function buildProjection(zones, tracks, width, height, padding = 24) {
  const points = [];

  zones.forEach((zone) => {
    (zone.points || []).forEach((point) => {
      if (Number.isFinite(point?.lat) && Number.isFinite(point?.lng)) points.push(point);
    });
  });
  tracks.forEach((track) => {
    track.points.forEach((point) => {
      if (Number.isFinite(point.lat) && Number.isFinite(point.lng)) points.push(point);
    });
  });

  if (points.length === 0) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  points.forEach((point) => {
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLng = Math.min(minLng, point.lng);
    maxLng = Math.max(maxLng, point.lng);
  });

  // Pad a degenerate extent (a single stationary parent) so it does not divide by zero.
  const latSpan = Math.max(maxLat - minLat, 0.0008);
  const lngSpan = Math.max(maxLng - minLng, 0.0008);
  const centerLat = (minLat + maxLat) / 2;

  // Longitude degrees shrink with latitude; without this the tracks look stretched.
  const lngScale = Math.cos((centerLat * Math.PI) / 180) || 1;
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;
  const scale = Math.min(usableWidth / (lngSpan * lngScale), usableHeight / latSpan);

  const offsetX = padding + (usableWidth - lngSpan * lngScale * scale) / 2;
  const offsetY = padding + (usableHeight - latSpan * scale) / 2;

  return (lat, lng) => ({
    x: offsetX + (lng - minLng) * lngScale * scale,
    // SVG y grows downward; latitude grows north.
    y: offsetY + (maxLat - lat) * scale,
  });
}

function StatTile({ label, value, tone }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #ececea', borderRadius: 10,
      padding: '10px 14px', minWidth: 96,
    }}>
      <div style={{ fontSize: 11, color: '#52514e', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: tone || '#0b0b0b', lineHeight: 1.2 }}>{value}</div>
    </div>
  );
}

export default function CarpoolReplayPage({ token, navigate }) {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [fromDate, setFromDate] = useState(() => isoDaysAgo(30));
  const [toDate, setToDate] = useState(todayIso);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [events, setEvents] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Playback
  const [currentMs, setCurrentMs] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(10);
  const [pov, setPov] = useState(null); // { actor_type, actor_id, actor_name }
  const [visibleGroups, setVisibleGroups] = useState(() => new Set(ALL_EVENT_GROUPS));

  const rafRef = useRef(null);
  const lastFrameRef = useRef(null);
  const feedRef = useRef(null);

  // Real basemap when a Maps key is configured; the SVG diagram is the fallback
  // and stays available as a toggle, since it reads better when the tracks span
  // a few hundred metres and street detail is just noise.
  const [mapMode, setMapMode] = useState(GOOGLE_MAPS_API_KEY ? 'map' : 'diagram');
  const [mapError, setMapError] = useState('');
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const zonePolysRef = useRef([]);
  const trackLinesRef = useRef(new Map());
  const markersRef = useRef(new Map());
  const trackCutoffRef = useRef(new Map());
  const fittedSessionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/schools', {}, token)
      .then((res) => { if (!cancelled) setSchools(res.data || []); })
      .catch(() => { if (!cancelled) setSchools([]); });
    return () => { cancelled = true; };
  }, [token]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (schoolId) params.set('school_id', schoolId);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      params.set('limit', '100');

      const res = await apiFetch(`/replay/sessions?${params.toString()}`, {}, token);
      setSessions(res.data || []);
    } catch (err) {
      setError(err.message || 'Could not load carpool sessions');
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, [fromDate, schoolId, toDate, token]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  /** Pull the whole run: detail, every event page, and the GPS tracks. */
  const openSession = useCallback(async (sessionId) => {
    setLoading(true);
    setError('');
    setPlaying(false);
    setPov(null);
    setSelectedSessionId(sessionId);

    try {
      const [detailRes, gpsRes] = await Promise.all([
        apiFetch(`/replay/sessions/${sessionId}`, {}, token),
        apiFetch(`/replay/sessions/${sessionId}/gps`, {}, token),
      ]);

      // The timeline is paginated by id; a busy run can exceed one page.
      const collected = [];
      let afterId = 0;
      for (let page = 0; page < 50; page += 1) {
        const res = await apiFetch(
          `/replay/sessions/${sessionId}/events?limit=${EVENT_PAGE_SIZE}&after_id=${afterId}`,
          {},
          token
        );
        collected.push(...(res.data || []));
        if (!res.has_more) break;
        afterId = res.next_after_id;
      }

      setDetail(detailRes.data);
      setEvents(collected);
      setTracks(gpsRes.data || []);
    } catch (err) {
      setError(err.message || 'Could not load this carpool session');
      setDetail(null);
      setEvents([]);
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // GPS is recorded in the background without events, so the playable range has
  // to span the tracks too, not just the event log.
  const timeline = useMemo(
    () => extendRangeWithTracks(buildTimeline(events), tracks),
    [events, tracks]
  );

  // Start at the beginning whenever a different run is opened.
  useEffect(() => {
    setCurrentMs(timeline.startMs);
  }, [timeline.startMs]);

  // Playback clock: advance wall-clock delta × speed, and stop at the end.
  useEffect(() => {
    if (!playing || timeline.startMs === null) return undefined;

    lastFrameRef.current = performance.now();
    const step = (now) => {
      const deltaMs = now - lastFrameRef.current;
      lastFrameRef.current = now;

      setCurrentMs((previous) => {
        const next = (previous ?? timeline.startMs) + deltaMs * speed;
        if (next >= timeline.endMs) {
          setPlaying(false);
          return timeline.endMs;
        }
        return next;
      });

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [playing, speed, timeline.endMs, timeline.startMs]);

  const atMs = currentMs ?? timeline.startMs ?? 0;

  const studentStates = useMemo(
    () => deriveStudentStates(timeline.events, atMs),
    [timeline.events, atMs]
  );
  // The roster size lets the waiting count include students who never moved and
  // so never produced an event.
  const totalStudents = Number(detail?.session?.student_count) || null;
  const statusCounts = useMemo(
    () => summarizeStatuses(studentStates, totalStudents),
    [studentStates, totalStudents]
  );
  const appStates = useMemo(
    () => deriveAppStates(timeline.events, atMs),
    [timeline.events, atMs]
  );

  const povEvents = useMemo(() => (
    pov ? filterEventsForActor(timeline.events, pov.actor_type, pov.actor_id) : timeline.events
  ), [pov, timeline.events]);

  // Counts come from the whole run for the current point of view, not just the
  // part played so far, so the filter labels do not shift while it is playing.
  const groupCounts = useMemo(() => countEventsByGroup(povEvents), [povEvents]);

  const filteredEvents = useMemo(
    () => povEvents.filter((event) => visibleGroups.has(eventGroup(event.event_type))),
    [povEvents, visibleGroups]
  );

  const visibleFeed = useMemo(() => {
    const cutoff = lastEventIndexAt(filteredEvents, atMs);
    return filteredEvents.slice(Math.max(0, cutoff + 1 - FEED_LIMIT), cutoff + 1).reverse();
  }, [atMs, filteredEvents]);

  const toggleGroup = (group) => {
    setVisibleGroups((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  // Keep the newest visible event at the top of the feed while playing.
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = 0;
  }, [visibleFeed.length]);

  const activeTracks = useMemo(() => (
    pov?.actor_type === 'contact'
      ? tracks.filter((track) => Number(track.contact_id) === Number(pov.actor_id))
      : tracks
  ), [pov, tracks]);

  const mapWidth = 640;
  const mapHeight = 420;
  const project = useMemo(
    () => buildProjection(detail?.zones || [], tracks, mapWidth, mapHeight),
    [detail, tracks]
  );

  // Create the map once the container exists, then draw the pickup zones on it.
  useEffect(() => {
    if (mapMode !== 'map' || !detail) return undefined;

    let cancelled = false;

    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapEl.current) return;

        if (!mapRef.current) {
          mapRef.current = new google.maps.Map(mapEl.current, {
            center: { lat: 25.7617, lng: -80.1918 },
            zoom: 15,
            mapTypeId: 'roadmap',
            clickableIcons: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: 'greedy',
          });
        }

        // Redraw the geofences for this session.
        zonePolysRef.current.forEach((poly) => poly.setMap(null));
        zonePolysRef.current = (detail.zones || [])
          .map((zone) => {
            const path = (zone.points || []).filter(
              (point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lng)
            );
            if (path.length < 3) return null;

            const polygon = new google.maps.Polygon({
              map: mapRef.current,
              paths: path,
              strokeColor: ZONE_COLOR,
              strokeOpacity: 0.95,
              strokeWeight: 2,
              fillColor: ZONE_COLOR,
              fillOpacity: 0.15,
              clickable: false,
              zIndex: 1,
            });
            return polygon;
          })
          .filter(Boolean);

        // Frame the whole run once: the zones plus every breadcrumb.
        if (fittedSessionRef.current !== selectedSessionId) {
          const bounds = new google.maps.LatLngBounds();
          let hasPoint = false;

          (detail.zones || []).forEach((zone) => {
            (zone.points || []).forEach((point) => {
              if (Number.isFinite(point?.lat) && Number.isFinite(point?.lng)) {
                bounds.extend(point);
                hasPoint = true;
              }
            });
          });
          tracks.forEach((track) => {
            track.points.forEach((point) => {
              bounds.extend({ lat: point.lat, lng: point.lng });
              hasPoint = true;
            });
          });

          if (hasPoint) {
            mapRef.current.fitBounds(bounds, 48);
            fittedSessionRef.current = selectedSessionId;
          }
        }

        setMapError('');
      })
      .catch((error) => {
        if (cancelled) return;
        setMapError(error.message || 'Unable to load the map.');
        setMapMode('diagram');
      });

    return () => { cancelled = true; };
  }, [detail, mapMode, selectedSessionId, tracks]);

  // Tear the overlays down when the map is hidden or the session changes, so a
  // previous run's tracks never linger on the next one.
  useEffect(() => {
    const lines = trackLinesRef.current;
    const markers = markersRef.current;
    const cutoffs = trackCutoffRef.current;

    return () => {
      lines.forEach((line) => line.setMap(null));
      lines.clear();
      markers.forEach((marker) => marker.setMap(null));
      markers.clear();
      cutoffs.clear();
    };
  }, [selectedSessionId, mapMode]);

  // Move the tracks and markers to match the cursor. Runs on every frame while
  // playing, so the polyline path is only rewritten when the number of
  // breadcrumbs behind the cursor actually changes.
  useEffect(() => {
    if (mapMode !== 'map' || !mapRef.current || !window.google?.maps) return;

    const google = window.google;
    const visible = new Set();

    activeTracks.forEach((track, index) => {
      const color = trackColor(index);
      const key = Number(track.contact_id);
      visible.add(key);

      const cutoff = lastPointIndexAt(track.points, atMs);
      const position = interpolatePosition(track.points, atMs);

      if (cutoff < 0 || !position) {
        trackLinesRef.current.get(key)?.setMap(null);
        markersRef.current.get(key)?.setMap(null);
        return;
      }

      let line = trackLinesRef.current.get(key);
      if (!line) {
        line = new google.maps.Polyline({
          strokeColor: color,
          strokeOpacity: 0.85,
          strokeWeight: 3,
          clickable: false,
          zIndex: 2,
        });
        trackLinesRef.current.set(key, line);
      }
      line.setMap(mapRef.current);

      if (trackCutoffRef.current.get(key) !== cutoff) {
        line.setPath(track.points.slice(0, cutoff + 1).map((point) => ({ lat: point.lat, lng: point.lng })));
        trackCutoffRef.current.set(key, cutoff);
      }

      let marker = markersRef.current.get(key);
      if (!marker) {
        marker = new google.maps.Marker({
          title: track.contact_name || `Parent #${key}`,
          zIndex: 3,
        });
        markersRef.current.set(key, marker);
      }
      marker.setMap(mapRef.current);
      marker.setPosition({ lat: position.lat, lng: position.lng });
      marker.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: position.stale ? 6 : 8,
        // A hollow marker means the phone stopped reporting — worth seeing, not
        // worth hiding.
        fillColor: position.stale ? '#ffffff' : color,
        fillOpacity: 1,
        strokeColor: color,
        strokeWeight: 3,
      });
    });

    // Hide tracks filtered out by the current point of view.
    trackLinesRef.current.forEach((line, key) => { if (!visible.has(key)) line.setMap(null); });
    markersRef.current.forEach((marker, key) => { if (!visible.has(key)) marker.setMap(null); });
  }, [activeTracks, atMs, mapMode]);

  const scrub = (value) => {
    setPlaying(false);
    setCurrentMs(timeline.startMs + Number(value));
  };

  const jumpToEvent = (event) => {
    setPlaying(false);
    setCurrentMs(event.ms);
  };

  // ---- Session list ----------------------------------------------------

  if (!selectedSessionId) {
    return (
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <button className="sa-btn sa-btn-secondary" onClick={() => navigate('dashboard')}>← Back</button>
          <h1 style={{ margin: 0, fontSize: 24 }}>Carpool Replay</h1>
        </div>
        <p style={{ color: '#52514e', marginTop: 0, marginBottom: 20 }}>
          Play back a finished carpool exactly as it happened — every status change,
          zone crossing, GPS track, and app interaction, with a point of view for
          each person involved.
        </p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 18 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#52514e' }}>
            School
            <select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} style={{ padding: 8, minWidth: 220 }}>
              <option value="">All schools</option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.school_nickname || school.legal_name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#52514e' }}>
            From
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: 8 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#52514e' }}>
            To
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: 8 }} />
          </label>
          <button className="sa-btn sa-btn-secondary" onClick={loadSessions}>Refresh</button>
        </div>

        {error && <div style={{ color: '#e34948', marginBottom: 12 }}>{error}</div>}
        {sessionsLoading && <div style={{ color: '#52514e' }}>Loading carpools…</div>}

        {!sessionsLoading && sessions.length === 0 && (
          <div style={{
            border: '1px dashed #d6d5d2', borderRadius: 10, padding: 32,
            textAlign: 'center', color: '#52514e',
          }}>
            No recorded carpools in this range. Runs are recorded automatically —
            start a dismissal and it will appear here.
          </div>
        )}

        {sessions.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ textAlign: 'left', fontSize: 12, color: '#52514e', borderBottom: '1px solid #ececea' }}>
                <th style={{ padding: 10 }}>Date</th>
                <th style={{ padding: 10 }}>School</th>
                <th style={{ padding: 10 }}>Started / Ended</th>
                <th style={{ padding: 10 }}>Duration</th>
                <th style={{ padding: 10 }}>Students</th>
                <th style={{ padding: 10 }}>Events</th>
                <th style={{ padding: 10 }}>GPS</th>
                <th style={{ padding: 10 }} />
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => {
                const startedMs = toMs(session.started_at);
                const endedMs = toMs(session.ended_at);
                return (
                  <tr key={session.id} style={{ borderBottom: '1px solid #f3f3f1' }}>
                    <td style={{ padding: 10, fontWeight: 600 }}>{session.dismissal_date}</td>
                    <td style={{ padding: 10 }}>{session.school_nickname || session.legal_name || session.school_id}</td>
                    <td style={{ padding: 10, fontSize: 13 }}>
                      {formatClock(startedMs)} → {endedMs ? formatClock(endedMs) : <em style={{ color: '#b45309' }}>in progress</em>}
                      <div style={{ fontSize: 11, color: '#52514e' }}>
                        by {session.started_by_name || session.started_trigger || 'system'}
                      </div>
                    </td>
                    <td style={{ padding: 10 }}>{endedMs ? formatDuration(endedMs - startedMs) : '—'}</td>
                    <td style={{ padding: 10 }}>
                      {session.student_count}
                      <span style={{ color: '#52514e', fontSize: 12 }}> ({session.sent_count} sent)</span>
                    </td>
                    <td style={{ padding: 10 }}>{session.event_count}</td>
                    <td style={{ padding: 10 }}>{session.gps_point_count}</td>
                    <td style={{ padding: 10 }}>
                      <button className="sa-btn sa-btn-primary" onClick={() => openSession(session.id)}>
                        ▶ Replay
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ---- Replay view -----------------------------------------------------

  const session = detail?.session;
  const elapsed = atMs - (timeline.startMs || 0);

  return (
    <div style={{ padding: 20, maxWidth: 1500, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          className="sa-btn sa-btn-secondary"
          onClick={() => { setSelectedSessionId(null); setDetail(null); setEvents([]); setTracks([]); }}
        >
          ← All carpools
        </button>
        <h1 style={{ margin: 0, fontSize: 20 }}>
          {session?.school_nickname || session?.legal_name || 'Carpool'} — {session?.dismissal_date}
        </h1>
        {session?.status === 'active' && (
          <span style={{ background: '#fef3c7', color: '#b45309', padding: '3px 10px', borderRadius: 999, fontSize: 12 }}>
            still in progress
          </span>
        )}
      </div>

      {loading && <div style={{ color: '#52514e' }}>Loading the run…</div>}
      {error && <div style={{ color: '#e34948' }}>{error}</div>}

      {!loading && detail && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <StatTile label="Waiting" value={statusCounts.waiting} tone={STATUS_COLORS.waiting} />
            <StatTile label="Called" value={statusCounts.called} tone={STATUS_COLORS.called} />
            <StatTile label="Sent" value={statusCounts.sent} tone={STATUS_COLORS.sent} />
            <StatTile label="Picked up" value={statusCounts.picked_up} tone={STATUS_COLORS.picked_up} />
            <StatTile label="Events" value={timeline.events.length} />
            <StatTile label="GPS points" value={tracks.reduce((sum, t) => sum + t.points.length, 0)} />
            <StatTile label="Duration" value={formatDuration(timeline.durationMs)} />
          </div>

          {/* Transport */}
          <div style={{
            background: '#fff', border: '1px solid #ececea', borderRadius: 10,
            padding: 14, marginBottom: 14,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
              <button
                className="sa-btn sa-btn-primary"
                onClick={() => {
                  // Replaying from the end should restart, not sit still.
                  if (atMs >= timeline.endMs) setCurrentMs(timeline.startMs);
                  setPlaying((was) => !was);
                }}
                style={{ minWidth: 96 }}
              >
                {playing ? '❚❚ Pause' : '▶ Play'}
              </button>
              <button className="sa-btn sa-btn-secondary" onClick={() => { setPlaying(false); setCurrentMs(timeline.startMs); }}>
                ⏮ Restart
              </button>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#52514e' }}>Speed</span>
                {SPEEDS.map((option) => (
                  <button
                    key={option}
                    onClick={() => setSpeed(option)}
                    className="sa-btn sa-btn-secondary"
                    style={{
                      padding: '4px 10px',
                      background: speed === option ? '#2a78d6' : undefined,
                      color: speed === option ? '#fff' : undefined,
                    }}
                  >
                    {option}×
                  </button>
                ))}
              </div>
              <div style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', fontSize: 15 }}>
                <strong>{formatClock(atMs)}</strong>
                <span style={{ color: '#52514e', marginLeft: 10 }}>
                  +{formatOffset(elapsed)} / {formatOffset(timeline.durationMs)}
                </span>
              </div>
            </div>

            <input
              type="range"
              min={0}
              max={Math.max(timeline.durationMs, 1)}
              value={Math.max(0, Math.min(elapsed, timeline.durationMs))}
              onChange={(e) => scrub(e.target.value)}
              style={{ width: '100%' }}
            />

            {/* Event density strip: where the run was busy, and what kind of busy. */}
            <div style={{ position: 'relative', height: 20, marginTop: 6 }}>
              {/* Same filter as the feed, so hiding a kind of event hides it
                  everywhere rather than only in the list. */}
              {timeline.events
                .filter((event) => visibleGroups.has(eventGroup(event.event_type)))
                .map((event) => (
                <div
                  key={event.id}
                  title={`${formatClock(event.ms)} — ${describeEvent(event)}`}
                  onClick={() => jumpToEvent(event)}
                  style={{
                    position: 'absolute',
                    left: `${timeline.durationMs > 0 ? (event.offsetMs / timeline.durationMs) * 100 : 0}%`,
                    top: eventGroup(event.event_type) === 'dismissal' ? 0 : 9,
                    width: 2,
                    height: eventGroup(event.event_type) === 'dismissal' ? 18 : 9,
                    background: GROUP_COLORS[eventGroup(event.event_type)],
                    opacity: event.ms <= atMs ? 0.95 : 0.25,
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
            {visibleGroups.size < ALL_EVENT_GROUPS.length && (
              <div style={{ fontSize: 11, color: '#52514e', marginTop: 2 }}>
                Showing {filteredEvents.length} of {povEvents.length} events
                {pov ? ' for this person' : ''}.
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr 380px', gap: 14, alignItems: 'start' }}>
            {/* POV picker */}
            <div style={{ background: '#fff', border: '1px solid #ececea', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: '#52514e', marginBottom: 8 }}>
                Point of view
              </div>
              <button
                onClick={() => setPov(null)}
                className="sa-btn sa-btn-secondary"
                style={{
                  width: '100%', textAlign: 'left', marginBottom: 6,
                  background: !pov ? '#2a78d6' : undefined, color: !pov ? '#fff' : undefined,
                }}
              >
                Everyone ({timeline.events.length})
              </button>

              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                {detail.actors.map((actor) => {
                  const key = `${actor.actor_type}:${actor.actor_id}`;
                  const isActive = pov && pov.actor_type === actor.actor_type
                    && Number(pov.actor_id) === Number(actor.actor_id);
                  const appState = appStates.get(key);

                  return (
                    <button
                      key={key}
                      onClick={() => setPov({
                        actor_type: actor.actor_type,
                        actor_id: actor.actor_id,
                        actor_name: actor.actor_name,
                      })}
                      className="sa-btn sa-btn-secondary"
                      style={{
                        width: '100%', textAlign: 'left', marginBottom: 6, padding: '8px 10px',
                        background: isActive ? '#2a78d6' : undefined,
                        color: isActive ? '#fff' : undefined,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {actor.actor_name || `${actor.actor_type} #${actor.actor_id}`}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.85 }}>
                        {actor.actor_type === 'contact' ? 'Parent' : (actor.actor_role || 'Staff')}
                        {' · '}{actor.event_count} events
                      </div>
                      {appState && (
                        <div style={{ fontSize: 11, opacity: 0.85 }}>
                          app: {appState.state}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Map */}
            <div style={{ background: '#fff', border: '1px solid #ececea', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: '#52514e' }}>
                  Positions at {formatClock(atMs)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#52514e' }}>
                    <span style={{
                      width: 12, height: 12, borderRadius: 3,
                      background: `${ZONE_COLOR}26`, border: `2px solid ${ZONE_COLOR}`,
                    }} />
                    Pickup zone
                  </span>
                  {GOOGLE_MAPS_API_KEY && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="sa-btn sa-btn-secondary"
                        onClick={() => setMapMode('map')}
                        style={{
                          padding: '3px 10px', fontSize: 12,
                          background: mapMode === 'map' ? '#2a78d6' : undefined,
                          color: mapMode === 'map' ? '#fff' : undefined,
                        }}
                      >
                        Map
                      </button>
                      <button
                        className="sa-btn sa-btn-secondary"
                        onClick={() => setMapMode('diagram')}
                        style={{
                          padding: '3px 10px', fontSize: 12,
                          background: mapMode === 'diagram' ? '#2a78d6' : undefined,
                          color: mapMode === 'diagram' ? '#fff' : undefined,
                        }}
                      >
                        Diagram
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {mapError && (
                <div style={{ fontSize: 12, color: '#b45309', marginBottom: 6 }}>{mapError}</div>
              )}

              {mapMode === 'map' && (
                <div
                  ref={mapEl}
                  style={{ width: '100%', height: mapHeight, borderRadius: 8, background: '#e9e7e3' }}
                />
              )}

              {mapMode === 'diagram' && !project && (
                <div style={{ padding: 40, textAlign: 'center', color: '#52514e' }}>
                  No GPS breadcrumbs or pickup zones recorded for this run.
                </div>
              )}

              {mapMode === 'diagram' && project && (
                <svg width="100%" viewBox={`0 0 ${mapWidth} ${mapHeight}`} style={{ background: '#fafaf9', borderRadius: 8 }}>
                  {/* Pickup zones */}
                  {(detail.zones || []).map((zone) => {
                    const path = (zone.points || [])
                      .filter((point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lng))
                      .map((point) => { const p = project(point.lat, point.lng); return `${p.x},${p.y}`; })
                      .join(' ');
                    if (!path) return null;
                    return (
                      <g key={zone.id}>
                        <polygon
                          points={path}
                          fill={ZONE_COLOR}
                          fillOpacity={0.15}
                          stroke={ZONE_COLOR}
                          strokeWidth={2}
                        />
                      </g>
                    );
                  })}

                  {/* Each parent's path so far, plus their marker at the cursor */}
                  {activeTracks.map((track, index) => {
                    const color = trackColor(index);
                    const travelled = track.points.filter((point) => toMs(point.t) <= atMs);
                    const path = travelled
                      .map((point) => { const p = project(point.lat, point.lng); return `${p.x},${p.y}`; })
                      .join(' ');
                    const position = interpolatePosition(track.points, atMs);
                    const marker = position ? project(position.lat, position.lng) : null;

                    return (
                      <g key={track.contact_id}>
                        {path && (
                          <polyline
                            points={path}
                            fill="none"
                            stroke={color}
                            strokeWidth={2}
                            strokeOpacity={0.65}
                            strokeLinejoin="round"
                          />
                        )}
                        {marker && (
                          <>
                            <circle
                              cx={marker.x}
                              cy={marker.y}
                              r={position.stale ? 5 : 7}
                              fill={position.stale ? '#fff' : color}
                              stroke={color}
                              strokeWidth={2}
                              strokeDasharray={position.stale ? '3 2' : undefined}
                            />
                            <text x={marker.x + 10} y={marker.y + 4} fontSize={11} fill="#0b0b0b">
                              {track.contact_name || `#${track.contact_id}`}
                              {position.stale ? ' (no signal)' : ''}
                            </text>
                          </>
                        )}
                      </g>
                    );
                  })}
                </svg>
              )}

              {/* Who is who on the map, and whether their phone is still reporting */}
              {activeTracks.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                  {activeTracks.map((track, index) => {
                    const position = interpolatePosition(track.points, atMs);
                    const color = trackColor(index);
                    return (
                      <button
                        key={track.contact_id}
                        onClick={() => setPov({
                          actor_type: 'contact',
                          actor_id: track.contact_id,
                          actor_name: track.contact_name,
                        })}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          border: 'none', background: 'transparent', cursor: 'pointer',
                          padding: 0, fontSize: 12,
                          // Dim a parent who has not appeared yet at this point in the run.
                          opacity: position ? 1 : 0.4,
                        }}
                        title={position ? 'Show only this parent' : 'Not yet reporting at this time'}
                      >
                        <span style={{
                          width: 11, height: 11, borderRadius: '50%',
                          background: position && !position.stale ? color : '#fff',
                          border: `2px solid ${color}`,
                        }} />
                        <span style={{ color: '#0b0b0b' }}>
                          {track.contact_name || `#${track.contact_id}`}
                        </span>
                        {position?.stale && (
                          <span style={{ color: '#b45309', fontSize: 11 }}>no signal</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Student board as it stood at the cursor */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: '#52514e', marginBottom: 8 }}>
                  Board at this moment
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                  {[...studentStates.values()].length === 0 && (
                    <span style={{ color: '#52514e', fontSize: 13 }}>No student had changed status yet.</span>
                  )}
                  {[...studentStates.values()]
                    .sort((a, b) => b.changed_at_ms - a.changed_at_ms)
                    .map((state) => (
                      <span
                        key={state.student_id}
                        title={`${STATUS_LABELS[state.status]} at ${formatClock(state.changed_at_ms)}${state.by_name ? ` by ${state.by_name}` : ''}`}
                        style={{
                          fontSize: 12, padding: '3px 9px', borderRadius: 999,
                          background: `${STATUS_COLORS[state.status]}1a`,
                          color: STATUS_COLORS[state.status],
                          border: `1px solid ${STATUS_COLORS[state.status]}55`,
                        }}
                      >
                        {state.student_name} · {STATUS_LABELS[state.status]}
                      </span>
                    ))}
                </div>
              </div>
            </div>

            {/* Event feed */}
            <div style={{ background: '#fff', border: '1px solid #ececea', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: '#52514e' }}>
                  {pov ? `${pov.actor_name || 'Selected person'}` : 'Everyone'}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    className="sa-btn sa-btn-secondary"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => setVisibleGroups(new Set(ALL_EVENT_GROUPS))}
                    disabled={visibleGroups.size === ALL_EVENT_GROUPS.length}
                  >
                    All
                  </button>
                  <button
                    className="sa-btn sa-btn-secondary"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => setVisibleGroups(new Set())}
                    disabled={visibleGroups.size === 0}
                  >
                    None
                  </button>
                </div>
              </div>

              {/* Pick which kinds of event to show. Each chip carries its count
                  for this point of view, so it is clear what hiding one costs. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {ALL_EVENT_GROUPS.map((group) => {
                  const isOn = visibleGroups.has(group);
                  const count = groupCounts[group] || 0;
                  const color = GROUP_COLORS[group];

                  return (
                    <button
                      key={group}
                      onClick={() => toggleGroup(group)}
                      // An empty group stays clickable so the choice sticks when
                      // switching to a person who does have those events.
                      title={count === 0 ? 'No events of this kind in this view' : undefined}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 11, padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
                        border: `1px solid ${isOn ? color : '#d6d5d2'}`,
                        background: isOn ? `${color}1a` : 'transparent',
                        color: isOn ? color : '#8a8884',
                        opacity: count === 0 ? 0.55 : 1,
                      }}
                    >
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: isOn ? color : 'transparent',
                        border: `1px solid ${isOn ? color : '#b6b4b0'}`,
                      }} />
                      {GROUP_LABELS[group]}
                      <span style={{ opacity: 0.75 }}>{count}</span>
                    </button>
                  );
                })}
              </div>

              <div ref={feedRef} style={{ maxHeight: 560, overflowY: 'auto' }}>
                {visibleFeed.length === 0 && (
                  <div style={{ color: '#52514e', fontSize: 13, padding: 10 }}>
                    {visibleGroups.size === 0
                      ? 'No event types selected.'
                      : (filteredEvents.length === 0
                        ? 'No events of the selected kinds in this view.'
                        : 'Nothing has happened yet at this point in the run.')}
                  </div>
                )}
                {visibleFeed.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => jumpToEvent(event)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', border: 'none',
                      borderLeft: `3px solid ${GROUP_COLORS[eventGroup(event.event_type)]}`,
                      background: 'transparent', padding: '6px 10px', cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 11, color: '#52514e', fontVariantNumeric: 'tabular-nums' }}>
                      {formatClock(event.ms)} · +{formatOffset(event.offsetMs)}
                      {event.app !== 'server' && <span> · {event.app}</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#0b0b0b' }}>{describeEvent(event)}</div>
                    {!pov && event.actor_name && (
                      <div style={{ fontSize: 11, color: '#52514e' }}>{event.actor_name}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
